import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../lib/api";
import {
  Bell, X, UserPlus, CalendarCheck, CalendarX, Theater, ThumbsDown, FileCheck, BellRing,
} from "lucide-react";

const TYPE_ICON = {
  application: UserPlus,
  audition_confirmed: CalendarCheck,
  audition_cant_attend: CalendarX,
  role_accepted: Theater,
  role_declined: ThumbsDown,
  contract_accepted: FileCheck,
  notification_response: BellRing,
};
const TYPE_COLOR = {
  application: "#8B5CF6",
  audition_confirmed: "#22C55E",
  audition_cant_attend: "#DC2626",
  role_accepted: "#10B981",
  role_declined: "#EF4444",
  contract_accepted: "#10B981",
  notification_response: "#F59E0B",
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const s = Math.floor((now - d) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m > 1 ? "s" : ""} ago`;
  const time = d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase().replace(" ", "");
  const h = Math.floor(m / 60);
  if (d.toDateString() === now.toDateString()) return h < 6 ? `${h} hour${h > 1 ? "s" : ""} ago` : `Today at ${time}`;
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return `Yesterday at ${time}`;
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} at ${time}`;
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = () =>
    adminApi.get("/admin/dashboard-notifications")
      .then(({ data }) => { setItems(data.items || []); setUnread(data.unread_count || 0); })
      .catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const onItem = async (n) => {
    if (!n.read) await adminApi.post(`/admin/dashboard-notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };
  const markAll = async () => { await adminApi.post("/admin/dashboard-notifications/read-all").catch(() => {}); load(); };
  const clearAll = async () => { await adminApi.delete("/admin/dashboard-notifications/clear-all").catch(() => {}); load(); };
  const clearOne = async (e, id) => { e.stopPropagation(); await adminApi.delete(`/admin/dashboard-notifications/${id}`).catch(() => {}); load(); };

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        data-testid="notifications-bell"
        title="Notifications"
        className="relative p-2 text-[#A19D94] hover:text-[#F2EBE1] transition-colors"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-blood text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="notifications-panel"
            className="absolute right-0 mt-2 w-[380px] max-h-[70vh] bg-[#0A0A0A] border border-[#262626] shadow-2xl z-50 flex flex-col animate-fade-in"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a] shrink-0">
              <span className="font-display uppercase tracking-wide text-sm">Notifications</span>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider">
                <button onClick={markAll} data-testid="notif-mark-all-read" className="text-[#A19D94] hover:text-[#F2EBE1]">Mark all read</button>
                <button onClick={clearAll} data-testid="notif-clear-all" className="text-[#6B6862] hover:text-blood">Clear all</button>
              </div>
            </div>
            <div className="overflow-y-auto">
              {items.length === 0 ? (
                <p className="text-sm text-[#6B6862] text-center py-10" data-testid="notif-empty">No notifications yet.</p>
              ) : (
                items.map((n) => {
                  const Icon = TYPE_ICON[n.type] || Bell;
                  const color = TYPE_COLOR[n.type] || "#A19D94";
                  return (
                    <button
                      key={n.id}
                      onClick={() => onItem(n)}
                      data-testid={`notif-item-${n.id}`}
                      className={`group w-full text-left flex gap-3 px-4 py-3 border-b border-[#141414] hover:bg-[#121212] transition-colors ${n.read ? "" : "bg-[#0d0d0d]"}`}
                    >
                      <span className="shrink-0 mt-0.5" style={{ color }}><Icon size={16} /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-[#F2EBE1] leading-snug">{n.message}</span>
                        <span className="block text-[11px] font-mono text-[#6B6862] mt-1">{relTime(n.created_at)}</span>
                      </span>
                      {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-blood mt-1.5" />}
                      <span
                        onClick={(e) => clearOne(e, n.id)}
                        role="button"
                        tabIndex={0}
                        data-testid={`notif-clear-${n.id}`}
                        className="shrink-0 text-[#3a3835] opacity-0 group-hover:opacity-100 hover:text-blood transition-opacity mt-0.5"
                      >
                        <X size={14} />
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
